import axios from "axios";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import styled, { useTheme } from "styled-components/macro";
import ReactTooltip from "react-tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import { endpoints } from "../../../endpoints";
import {
  useStoreState,
  convertUserToOption,
  rowHover,
} from "../../../util/util";
import type {
  LeadAssignee,
  OptionType,
  User,
  UUID,
} from "../../../types/types";
import { PrimaryButtonFitContainer } from "../../../components/Buttons/Buttons";
import { SearchSelectInfiniteScroll } from "../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { Form } from "../../../layout/FormLayout";
import { Table } from "../../../components/Table/Table";
import { InfoIcon, DeleteIcon } from "../../../components/Icons/Icons";
import { useCountriesList } from "../../../util/Locations";
import { getCountryOption } from "../../../util/location";
import { spacingTokens } from "../../../designTokens";

const ActionCellWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

type Props = {
  url: string;
  initialSubscribers: LeadAssignee[];
  onSuccess: () => void;
  onError: (error: unknown) => void;
};

const MultiValue = () => null;

export function AssignLeadSubscribers({
  url,
  initialSubscribers,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tableData, setTableData] = useState(() =>
    initialSubscribers.map((user) => ({
      ...user,
      label: convertUserToOption(user).label,
    }))
  );
  const theme = useTheme();
  const countries = useCountriesList();

  const { handleSubmit, control, watch, formState, setValue } = useForm({
    defaultValues: {
      selectedUsers: initialSubscribers.map((user) => ({
        label: convertUserToOption(user).label,
        value: user,
      })),
    },
  });

  const watchedSelectedUsers = watch("selectedUsers");

  const onSubmit = async (data: {
    selectedUsers: OptionType<LeadAssignee>[];
  }) => {
    try {
      setIsSubmitting(true);
      const subscriber_ids = data.selectedUsers.map((user) => user.value.id);
      await axios.patch(url, { subscriber_ids });
      onSuccess();
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Memoized filtering logic to prevent duplicate user selections
  const getFilteredUserOptions = useMemo(() => {
    return (response: User[]) => {
      const selectedUserIds = (watchedSelectedUsers || []).map(
        (user) => user.value.id
      );
      return response
        .filter((user) => !selectedUserIds.includes(user.id))
        .map((user) => ({
          label: convertUserToOption(user).label,
          value: user,
        }));
    };
  }, [watchedSelectedUsers]);

  const handleRemoveUser = useCallback(
    (userId: UUID) => {
      const currentUsers = watchedSelectedUsers || [];
      const updatedUsers = currentUsers.filter(
        (user) => user.value.id !== userId
      );
      setValue("selectedUsers", updatedUsers);
    },
    [setValue, watchedSelectedUsers]
  );

  const columns = useMemo<ColumnDef<LeadAssignee & { label: string }>[]>(
    () => [
      {
        id: "userName",
        header: t("Username"),
        accessorFn: (row) => row.label.split(" - ")[0],
      },
      {
        id: "emailId",
        header: t("Email ID"),
        accessorFn: (row) => row?.email_address || row.label.split(" - ")[1],
      },
      {
        id: "actions",
        header: t("Actions"),
        cell: ({ row }) => {
          const user = row.original;
          const tooltipId = `user-info-${user.id}`;

          const businessUnit = user?.business_unit || "N/A";
          const country = user?.country
            ? getCountryOption(countries, user.country)?.label
            : "N/A";

          return (
            <ActionCellWrapper>
              <IconButton type="button" data-tip data-for={tooltipId}>
                <InfoIcon fill={theme.resolve("color_icon_primary")} />
              </IconButton>
              <ReactTooltip id={tooltipId} effect="solid" multiline={true}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: spacingTokens.spacing_xs,
                    alignItems: "flex-start",
                  }}
                >
                  <span>Business Unit: {businessUnit}</span>
                  <span>Country: {country}</span>
                </div>
              </ReactTooltip>
              <IconButton
                type="button"
                onClick={() => handleRemoveUser(user.id)}
              >
                <DeleteIcon fill={theme.errorColor} />
              </IconButton>
            </ActionCellWrapper>
          );
        },
      },
    ],
    [countries, handleRemoveUser, t, theme]
  );

  useEffect(() => {
    setTableData(
      (watchedSelectedUsers || []).map((user) => ({
        ...user.value,
        label: user.label,
      }))
    );
  }, [watchedSelectedUsers]);

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="selectedUsers"
          control={control}
          render={({ onChange, value }) => (
            <SearchSelectInfiniteScroll
              label={t("Select Users")}
              placeholder={t("Select Users")}
              isMulti
              value={value}
              onChange={onChange}
              baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                storefront_id,
                tenant_id
              )}
              getOptions={getFilteredUserOptions}
              params={new URLSearchParams({ is_active: "true" })}
              filterOption={() => true}
              formState={formState}
              errors={formState.errors}
              components={{ MultiValue }}
            />
          )}
        />

        {tableData.length > 0 && (
          <div style={{ marginTop: spacingTokens.spacing_2xl }}>
            <Table
              columns={columns}
              data={tableData}
              isLoading={false}
              error={undefined}
              rowHover={rowHover}
            />
          </div>
        )}

        <PrimaryButtonFitContainer
          type="submit"
          loading={isSubmitting}
          style={{ marginTop: spacingTokens.spacing_2xl }}
        >
          {t("Save")}
        </PrimaryButtonFitContainer>
      </Form>
    </>
  );
}
